import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canAccess, firstAllowedPath } from "@/lib/pages";
import LoginPage from "@/pages/LoginPage";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import PartiesPage from "@/pages/PartiesPage";
import ProductsPage from "@/pages/ProductsPage";
import YarnPage from "@/pages/YarnPage";
import BatchesPage from "@/pages/BatchesPage";
import CuttingPage from "@/pages/CuttingPage";
import StitchingPage from "@/pages/StitchingPage";
import PackingPage from "@/pages/PackingPage";
import StockPage from "@/pages/StockPage";
import DispatchPage from "@/pages/DispatchPage";
import AccessoriesPage from "@/pages/AccessoriesPage";
import LedgerPage from "@/pages/LedgerPage";
import UsersPage from "@/pages/UsersPage";
import WorkersPage from "@/pages/WorkersPage";
import ReportsPage from "@/pages/ReportsPage";
import DCPage from "@/pages/DCPage";
import CompanySettingsPage from "@/pages/CompanySettingsPage";
import LotTimelinePage from "@/pages/LotTimelinePage";
import OpsView from "@/pages/OpsView";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="label-caps animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PageGate({ pageKey, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!canAccess(user, pageKey)) {
    const dest = firstAllowedPath(user);
    if (dest === loc.pathname) return null;
    return <Navigate to={dest} replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/ops"
            element={
              <Protected>
                <OpsView />
              </Protected>
            }
          />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<PageGate pageKey="dashboard"><DashboardPage /></PageGate>} />
            <Route path="parties" element={<PageGate pageKey="parties"><PartiesPage /></PageGate>} />
            <Route path="products" element={<PageGate pageKey="products"><ProductsPage /></PageGate>} />
            <Route path="yarn" element={<PageGate pageKey="yarn"><YarnPage /></PageGate>} />
            <Route path="batches/knitting" element={<PageGate pageKey="knitting"><BatchesPage /></PageGate>} />
            <Route path="batches/dyeing" element={<PageGate pageKey="dyeing"><BatchesPage /></PageGate>} />
            <Route path="batches/compacting" element={<PageGate pageKey="compacting"><BatchesPage /></PageGate>} />
            <Route path="batches/printing" element={<PageGate pageKey="printing"><BatchesPage /></PageGate>} />
            <Route path="cutting" element={<PageGate pageKey="cutting"><CuttingPage /></PageGate>} />
            <Route path="stitching" element={<PageGate pageKey="stitching"><StitchingPage /></PageGate>} />
            <Route path="packing" element={<PageGate pageKey="packing"><PackingPage /></PageGate>} />
            <Route path="stock" element={<PageGate pageKey="stock"><StockPage /></PageGate>} />
            <Route path="dispatch" element={<PageGate pageKey="dispatch"><DispatchPage /></PageGate>} />
            <Route path="accessories" element={<PageGate pageKey="accessories"><AccessoriesPage /></PageGate>} />
            <Route path="ledger" element={<PageGate pageKey="ledger"><LedgerPage /></PageGate>} />
            <Route path="users" element={<AdminOnly><UsersPage /></AdminOnly>} />
            <Route path="workers" element={<AdminOnly><WorkersPage /></AdminOnly>} />
            <Route path="reports" element={<PageGate pageKey="reports"><ReportsPage /></PageGate>} />
            <Route path="dcs" element={<PageGate pageKey="dcs"><DCPage /></PageGate>} />
            <Route path="settings/company" element={<AdminOnly><CompanySettingsPage /></AdminOnly>} />
            <Route path="lots" element={<LotTimelinePage />} />
            <Route path="lots/:lotNo" element={<LotTimelinePage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
